<template>
  <v-flex>
      <div ref="content">
      <v-card v-if="!justPlot">
          <v-card-text v-if="loading">
              <v-layout style="min-height:40px;">
                  <loading-progress-bar :loading="loading" color="secondary"></loading-progress-bar>
              </v-layout>
          </v-card-text>
          <v-card-text v-if="type==='dygraph'">
              <dygraphs-component :df="df" ref="dygraphplot"></dygraphs-component>
          </v-card-text>
          <v-card-text v-else>
              <vue-plotly v-if="!loading&&data&&data.length>0" :data="data" :show-export="showExport" :disable-responsive="disableResponsive" :layout="layout" :options="options" ref="plotly"/>
          </v-card-text>
      </v-card>
          <vue-plotly v-if="justPlot&&!loading&&data&&data.length>0" :show-export="showExport" :data="data" :disable-responsive="disableResponsive" :layout="layout" :options="options" ref="plotly"/>
      </div>
  </v-flex>
</template>

<script>
import VuePlotly from './VuePlotly'
import {getTimeSeries} from '@/helper'
import DygraphsComponent from './DygraphsComponent'
import LoadingProgressBar from './LoadingProgressBar'
import smoothLine from "./mixins/smoothLine";
import unitResolver from "./mixins/unitResolver";

export default {
    props: {
        title: {type: String, default: ''},
        variablesList: {type: Array, default: null},
        variableX: {type: String, default: 'timestamp'},
        dateRange: {type: Object, default: null},
        layoutOverrides: {type: Object, default: null},
        showExport: {type: Boolean, default: true},
        showUnits: {type: Boolean, default: false},
        options: {
            type: Object, default: function () {
                return {}
            }
        },
        equipmentName: {type: String, default: 'Chiller 1'},
        type: {type: String, default: 'scattergl'},
        onOnly: {type: Boolean, default: false},
        justPlot: {type: Boolean, default: false},
        disableResponsive: {type: Boolean, default: false},
        padDates: {type: Boolean, default: false},
        xScalingFactor: {type: Number, default: 1},
        yScalingFactor: {type: Number, default: 1}
  },
    mixins:[smoothLine,unitResolver],
  components: {
    LoadingProgressBar,
    DygraphsComponent,
    VuePlotly
  },
  computed: {
    layout(){
        let settings={
            title: this.title,
            xaxis: {
                title: "Time"
            },
            legend: {
                orientation: 'h',
                y:-0.3
            },
            hovermode:"x",
            hoverlabel: {
                namelength: -1,
            },
            height:300,
            showlegend:true,
            margin:{
                l: 50,
                r: 50,
                t: 30,
                pad: 1
            },
        };
        if(this.layoutOverrides){
            settings=Object.assign({}, settings, this.layoutOverrides);
        }
      return settings;
    }
  },
  name: 'MultiPlotChart',
  watch: {
    variablesList () {
      this.refresh()
    },
    dateRange () {
      this.refresh()
    },
      equipmentName() {
          this.refresh()
      },
    onOnly () {
      this.refresh()
    }
  },
  data: function () {
    return {
      data:[],
      timeout:null,
      facility_name:null,
      df: null,
      loading: true
    }
    },
  methods: {
      debounce(callback, wait) {
          clearTimeout(this.timeout);
          this.timeout= setTimeout(() => callback(), wait);
      },
      refresh(){
          this.debounce(this.doRefresh,200);
      },
      doRefresh() {
        let me=this;
        let hasCalculatedVars=false;
      this.loading=true
      if (!!this.variablesList && !!this.facility_name){
        this.data = []
          let promises=[];
        for (let i in this.variablesList) {
            if(this.variablesList[i].calculated){
                hasCalculatedVars=true;
                continue;
            }

            let equipment_name = this.variablesList[i].equipment_name ? this.variablesList[i].equipment_name : this.equipmentName
            let varsArray = [this.variablesList[i].name]
            if (this.variablesList[i].variableX) {
                varsArray.unshift(this.variablesList[i].variableX);
            }
            let agg=this.variablesList[i].agg ? this.variablesList[i].agg : null;
            let apiBaseUrl=this.variablesList[i].apiBaseUrl ? this.variablesList[i].apiBaseUrl : null;
            let facilityName=this.variablesList[i].facility_name ? this.variablesList[i].facility_name : this.facility_name;
            promises.push(getTimeSeries(
                varsArray,
                equipment_name,
                facilityName,
                this.dateRange,
                agg,
                this.onOnly || this.variablesList[i].on_only,
                null,
                false,
                apiBaseUrl,
                this.padDates
            ));
        }
            Promise.all(promises).then(function(reses) {
                let newData=[];
                let customXVariable=null;
                let m=0
                let colorList = ['#1f77b4',  // muted blue
                    '#ff7f0e',  // safety orange
                    '#2ca02c',  // cooked asparagus green
                    '#d62728',  // brick red
                    '#9467bd',  // muted purple
                    '#8c564b',  // chestnut brown
                    '#e377c2',  // raspberry yogurt pink
                    '#7f7f7f',  // middle gray
                    '#bcbd22',  // curry yellow-green
                    '#17becf']
                let compensator=0
                for (let i in me.variablesList) {
                    if(me.variablesList[i].calculated){
                        hasCalculatedVars=true;
                        compensator-=1
                        continue;
                    }
                    let incr=parseInt(i)+parseInt(compensator)
                    let df=reses[incr];
                    if(me.variablesList[i].useAsXVariable){
                        customXVariable=df[me.variablesList[i].name];
                    } else {
                        let x=me.variablesList[i].variableX ? df[me.variablesList[i].variableX] : customXVariable ? customXVariable : df[me.variableX]
                        let y=df[me.variablesList[i].name]
                        if(!x||!y||x.length===0||x.length===0){
                            continue
                        }
                        if(me.xScalingFactor!=1){
                          for (let j= 0; j < x.length; j++) {
                            x[j] = x[j] * me.xScalingFactor
                          }
                        }



                        if(me.yScalingFactor!=1){
                          for (let j= 0; j < y.length; j++) {
                            y[j] = y[j] * me.xScalingFactor
                          }
                        }
                        let name=me.variablesList[i].equipment_name? me.variablesList[i].equipment_name +' - ' + me.variablesList[i].name : me.equipmentName +' - ' + me.variablesList[i].name
                        let intialName=name
                        if(me.variablesList[i].name_override){
                            name=me.variablesList[i].name_override
                        }
                        if (me.showUnits){
                            let unit=me.getUnit(me.variablesList[i].name,me.variablesList[i].equipment_name)
                            let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                            name=name+unitbox
                        }
                        if(me.variablesList[i].add_smooth){
                            let options = me.variablesList[i].smooth_options ? me.variablesList[i].smooth_options : {span: 0.6, band: 0.8, degree: 1}
                            let cleanedX=[]
                            let cleanedY=[]
                            let xMinThreshold=me.variablesList[i].x_min_threshold ? me.variablesList[i].x_min_threshold : 0
                            let yMinThreshold=me.variablesList[i].y_min_threshold ? me.variablesList[i].y_min_threshold : 0
                            for(let valindex in x){
                                if(x[valindex]>xMinThreshold&&y[valindex]>yMinThreshold){
                                    if((!me.variablesList[i].x_max_threshold||x[valindex]<me.variablesList[i].x_max_threshold)&&
                                        (!me.variablesList[i].y_max_threshold||y[valindex]<me.variablesList[i].y_max_threshold)
                                    ){
                                        cleanedX.push(x[valindex])
                                        cleanedY.push(y[valindex])
                                    }

                                }
                            }
                            if(cleanedX.length>5&&cleanedY.length>5){
                                newData.push(
                                    {
                                        x: cleanedX,
                                        y: cleanedY,
                                        type: me.type,
                                        mode: me.variablesList[i].mode ? me.variablesList[i].mode : 'lines',
                                        opacity:me.variablesList[i].opacity ? me.variablesList[i].opacity : 1.0,
                                        name: name,
                                        intialName: intialName,
                                        xbins:me.variablesList[i].xbins ? me.variablesList[i].xbins : {},
                                        ybins:me.variablesList[i].ybins ? me.variablesList[i].ybins : {},
                                        hovertext:me.variablesList[i].useXAsHover ? df[me.variableX] : null,
                                        stackgroup:me.variablesList[i].stackgroup ? me.variablesList[i].stackgroup : null,
                                        yaxis:me.variablesList[i].yaxis ? me.variablesList[i].yaxis : null,
                                        line:me.variablesList[i].line ? me.variablesList[i].line : {},
                                    }
                                )
                                let smooth = me.smoothLineLoess(cleanedX, cleanedY, 50, options)
                                newData.push({
                                    line: {
                                        color: colorList[m % 10]
                                    },
                                    x: smooth.data['x'],
                                    y: smooth.fitted['fitted'],
                                    mode: 'lines',
                                    name: 'Smoothed Line of ' + name,
                                })
                            } else {
                              continue
                            }

                        } else {
                            newData.push(
                                {
                                    x: x,
                                    y: y,
                                    type: me.type,
                                    mode: me.variablesList[i].mode ? me.variablesList[i].mode : 'lines',
                                    opacity:me.variablesList[i].opacity ? me.variablesList[i].opacity : 1.0,
                                    name: name,
                                    intialName: intialName,
                                    xbins:me.variablesList[i].xbins ? me.variablesList[i].xbins : {},
                                    ybins:me.variablesList[i].ybins ? me.variablesList[i].ybins : {},
                                    hovertext:me.variablesList[i].useXAsHover ? df[me.variableX] : null,
                                    stackgroup:me.variablesList[i].stackgroup ? me.variablesList[i].stackgroup : null,
                                    yaxis:me.variablesList[i].yaxis ? me.variablesList[i].yaxis : null,
                                    line:me.variablesList[i].line ? me.variablesList[i].line : {},
                                }
                            )
                        }
                    }
                    m++

                }
                newData.sort((a, b) => (a.name > b.name) ? 1 : -1)
                if(hasCalculatedVars&&me.variableX==="timestamp"){
                    let timestamp=newData[0].x
                    for (let item of newData){
                        timestamp=timestamp.filter(t=>item.x.includes(t))
                        item.visible="legendonly"
                    }
                    let commonFrame={
                        "timestamp":timestamp
                    }
                    for (let item of newData){
                        commonFrame[item.intialName]=item.y.filter((value,index)=>timestamp.includes(item.x[index]))
                    }
                    let commonFrameKeys=Object.keys(commonFrame)
                    for (let i in me.variablesList) {
                        if (me.variablesList[i].calculated) {
                            let fabricatedY=[]
                            for (let index in timestamp){
                                var df={}
                                for (let key of commonFrameKeys){
                                    df[key]=commonFrame[key][index]
                                }
                                fabricatedY.push(eval(me.variablesList[i].formula))
                            }
                            newData.push(
                                {
                                    x: timestamp,
                                    y: fabricatedY,
                                    type: me.type,
                                    mode: me.variablesList[i].mode ? me.variablesList[i].mode : 'lines',
                                    opacity:me.variablesList[i].opacity ? me.variablesList[i].opacity : 1.0,
                                    name: me.variablesList[i].name,
                                    xbins:me.variablesList[i].xbins ? me.variablesList[i].xbins : {},
                                    ybins:me.variablesList[i].ybins ? me.variablesList[i].ybins : {},
                                    stackgroup:me.variablesList[i].stackgroup ? me.variablesList[i].stackgroup : null,
                                    yaxis:me.variablesList[i].yaxis ? me.variablesList[i].yaxis : null,
                                    line:me.variablesList[i].line ? me.variablesList[i].line : {},
                                }
                            )
                        }
                    }
                }
                me.data=newData;
                me.loading=false;
            });
      } else {
          this.loading=false
      }

    }
  },
   mounted () {
      this.facility_name = this.$store.state.pointsListStore.pointsList.facility_name
      this.refresh()
  }
}

</script>

<style scoped>

</style>
